/**
 * Journey Store — Persona-based context switching for the GRC client portal
 *
 * Controls which persona context is active, driving the sidebar navigation.
 * Each persona (Dirigeant, CISO, DPO, etc.) sees a curated set of modules.
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type { ContextId, AppMode } from '@/types/real-estate'

interface JourneyState {
  activeContext: ContextId
  mode: AppMode
  activeDossierId: string | null

  // Actions
  setContext: (context: ContextId) => void
  setMode: (mode: AppMode) => void
  toggleMode: () => void
  setActiveDossier: (dossierId: string | null) => void
  reset: () => void
}

const initialState = {
  activeContext: 'dirigeant' as ContextId,
  mode: 'client' as AppMode,
  activeDossierId: null as string | null,
}

export const useJourneyStore = create<JourneyState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        setContext: (context) =>
          set({ activeContext: context }, false, 'setContext'),

        setMode: (mode) =>
          set({ mode }, false, 'setMode'),

        toggleMode: () =>
          set(
            (state) => ({ mode: state.mode === 'client' ? 'pro' : 'client' }),
            false,
            'toggleMode'
          ),

        setActiveDossier: (dossierId) =>
          set({ activeDossierId: dossierId }, false, 'setActiveDossier'),

        reset: () => set(initialState, false, 'reset'),
      }),
      {
        name: 'sgrc-journey',
        partialize: (state) => ({
          activeContext: state.activeContext,
          mode: state.mode,
        }),
      }
    ),
    { name: 'JourneyStore' }
  )
)
